<template>
  <div v-loading="loading" class="workbench">
    <!-- 头部档案卡 -->
    <div class="profile-card">
      <div class="profile-card__main">
        <div class="avatar">{{ avatarText }}</div>
        <div class="profile-meta">
          <div class="profile-meta__row">
            <span class="profile-name">{{ data?.name || '-' }}</span>
            <el-tag size="small" effect="light" type="primary">{{ levelLabel }}</el-tag>
            <el-tag size="small" effect="light" type="success">{{ statusLabel }}</el-tag>
            <el-tag v-if="regionTag" size="small" effect="light" type="warning">{{ regionTag }}</el-tag>
          </div>
          <div class="profile-meta__sub">
            负责区域：{{ data?.regionName || '-' }} · 名下商家 {{ data?.merchantCount || 0 }} 家 · 累计分润 ¥{{
              fen2yuan(data?.totalProfit)
            }}
          </div>
        </div>
      </div>
      <el-button class="settle-btn" type="primary" @click="goSettle">我的结算单 →</el-button>
    </div>

    <!-- KPI -->
    <el-row :gutter="12" class="mb-16px">
      <el-col :xs="12" :sm="12" :md="6" v-for="item in kpis" :key="item.label">
        <div class="kpi-card">
          <div class="kpi-card__icon" :style="{ background: item.bg, color: item.color }">
            <Icon :icon="item.icon" :size="20" />
          </div>
          <div>
            <div class="kpi-card__value">{{ item.value }}</div>
            <div class="kpi-card__label">{{ item.label }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <!-- 分润贡献 -->
      <el-col :xs="24" :md="14">
        <el-card shadow="never" class="panel-card">
          <template #header>
            <div class="panel-head">
              <span>各软件分润贡献（{{ data?.currentPeriod || '-' }}）</span>
              <el-button link type="primary" @click="goLedger">查看分润明细 →</el-button>
            </div>
          </template>
          <div v-if="softwareProfits.length" class="profit-panel">
            <Echart class="profit-panel__chart" :height="260" :options="chartOptions" />
            <div class="profit-panel__legend">
              <div v-for="(item, idx) in softwareProfits" :key="item.softwareId" class="legend-row">
                <span class="legend-dot" :style="{ background: chartColors[idx % chartColors.length] }"></span>
                <span class="legend-name">{{ item.softwareName || item.softwareId }}</span>
                <span class="legend-amt">¥{{ fen2yuan(item.amount) }}</span>
              </div>
            </div>
          </div>
          <div v-else class="profit-empty">
            <el-empty description="本账期暂无分润" :image-size="88">
              <template #description>
                <div class="profit-empty__title">本账期暂无分润</div>
                <div class="profit-empty__desc">名下商家产生交易后将按各软件佣金规则计入</div>
              </template>
            </el-empty>
          </div>
        </el-card>
      </el-col>

      <!-- 快捷操作 -->
      <el-col :xs="24" :md="10">
        <el-card shadow="never" class="panel-card">
          <template #header>快捷操作</template>
          <div class="quick-grid">
            <div v-for="act in actions" :key="act.label" class="quick-item" @click="act.go()">
              <div class="quick-item__icon" :style="{ background: act.bg, color: act.color }">
                <Icon :icon="act.icon" :size="22" />
              </div>
              <div class="quick-item__label">{{ act.label }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { ElMessage } from 'element-plus'
import {
  buildMerchantBelongingInviteUrl,
  exportPortalLedgers,
  getPortalWorkbench,
  type AgentPortalWorkbenchVO
} from '@/api/agent/portal'
import download from '@/utils/download'

defineOptions({ name: 'AgentPortalWorkbench' })

const router = useRouter()
const loading = ref(false)
const data = ref<AgentPortalWorkbenchVO>()
const exporting = ref(false)

const chartColors = ['#3B82F6', '#22C55E', '#F59E0B', '#8B5CF6', '#06B6D4', '#EF4444']

const fen2yuan = (fen?: number) => {
  if (fen == null) return '0'
  return (Number(fen) / 100).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

const avatarText = computed(() => {
  const name = data.value?.name || ''
  return name ? name.slice(0, 1) : '代'
})

const levelLabel = computed(() => {
  const level = data.value?.level
  if (level === 2) return '二级代理'
  return '一级代理'
})

const statusLabel = computed(() => {
  const map: Record<number, string> = { 0: '待审核', 1: '正常', 2: '已驳回', 3: '已停用' }
  return map[data.value?.status ?? 1] || '正常'
})

const regionTag = computed(() => {
  const region = data.value?.regionName
  if (!region) return ''
  return data.value?.regionExclusive ? `${region} 独占` : region
})

const softwareProfits = computed(() => data.value?.softwareProfits || [])

const kpis = computed(() => {
  const d = data.value
  const waitCount = d?.waitConfirmCount || 0
  return [
    {
      label: `${d?.currentPeriod || '-'} 账期分润（未归集+已归集）`,
      value: fen2yuan(d?.monthProfit),
      icon: 'ep:money',
      color: '#2563EB',
      bg: 'rgba(37, 99, 235, 0.12)'
    },
    {
      label: '待归集分润',
      value: fen2yuan(d?.unsettledProfit),
      icon: 'ep:timer',
      color: '#D97706',
      bg: 'rgba(217, 119, 6, 0.12)'
    },
    {
      label: '名下商家',
      value: String(d?.merchantCount ?? 0),
      icon: 'ep:shop',
      color: '#0D9488',
      bg: 'rgba(13, 148, 136, 0.12)'
    },
    {
      label: waitCount > 0 ? `${waitCount} 张待确认结算单` : '无待确认结算单',
      value: waitCount > 0 ? `¥${fen2yuan(d?.waitConfirmAmount)}` : '¥0',
      icon: 'ep:wallet',
      color: '#7C3AED',
      bg: 'rgba(124, 58, 237, 0.12)'
    }
  ]
})

const chartOptions = computed<EChartsOption>(() => {
  const items = softwareProfits.value
  const total = items.reduce((sum, i) => sum + (i.amount || 0), 0)
  return {
    color: chartColors,
    tooltip: {
      trigger: 'item',
      formatter: (p: any) =>
        `${p.name}<br/>¥${Number(p.value || 0).toLocaleString('zh-CN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        })}`
    },
    series: [
      {
        type: 'pie',
        radius: ['52%', '74%'],
        center: ['50%', '50%'],
        label: { show: false },
        labelLine: { show: false },
        data: items.map((i) => ({
          name: i.softwareName || i.softwareId || '-',
          value: Number(((i.amount || 0) / 100).toFixed(2))
        }))
      }
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '42%',
        style: { text: '本月合计', textAlign: 'center', fill: '#909399', fontSize: 12 }
      },
      {
        type: 'text',
        left: 'center',
        top: '52%',
        style: {
          text: `¥${fen2yuan(total)}`,
          textAlign: 'center',
          fill: '#303133',
          fontSize: 18,
          fontWeight: 700
        }
      }
    ]
  }
})

const copyInvite = async () => {
  const agentNo = (data.value?.agentNo || localStorage.getItem('agent_portal_no') || '').trim()
  if (!agentNo) {
    ElMessage.warning('缺少代理编号，请重新登录后再复制拓客链接')
    return
  }
  const url = buildMerchantBelongingInviteUrl(agentNo)
  await navigator.clipboard.writeText(url)
  ElMessage.success(`已复制：${url}`)
}

const exportLedger = async () => {
  if (exporting.value) return
  exporting.value = true
  try {
    const blob = await exportPortalLedgers()
    download.excel(blob, '分润明细.xls')
    ElMessage.success('导出成功')
  } finally {
    exporting.value = false
  }
}

const goSettle = () => router.push('/agent-portal/settle')
const goLedger = () => router.push('/agent-portal/ledger')
const goAccount = () => router.push('/agent-portal/account')
const goSubs = () => router.push('/agent-portal/subs')
const goMerchants = () => router.push('/agent-portal/merchants')

const actions = [
  {
    label: '拓客邀请函',
    icon: 'ep:present',
    color: '#D97706',
    bg: 'rgba(217, 119, 6, 0.12)',
    go: copyInvite
  },
  {
    label: '分润明细导出',
    icon: 'ep:download',
    color: '#2563EB',
    bg: 'rgba(37, 99, 235, 0.12)',
    go: exportLedger
  },
  {
    label: '结算单查询',
    icon: 'ep:document',
    color: '#0D9488',
    bg: 'rgba(13, 148, 136, 0.12)',
    go: goSettle
  },
  {
    label: '收款账户',
    icon: 'ep:office-building',
    color: '#7C3AED',
    bg: 'rgba(124, 58, 237, 0.12)',
    go: goAccount
  },
  {
    label: '二级代理管理',
    icon: 'ep:user',
    color: '#DC2626',
    bg: 'rgba(220, 38, 38, 0.12)',
    go: goSubs
  },
  {
    label: '区域商家地图',
    icon: 'ep:map-location',
    color: '#0891B2',
    bg: 'rgba(8, 145, 178, 0.12)',
    go: goMerchants
  }
]

onMounted(async () => {
  loading.value = true
  try {
    data.value = (await getPortalWorkbench()) || {}
    if (data.value?.name) {
      localStorage.setItem('agent_portal_name', data.value.name)
    }
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.workbench {
  min-height: 100%;
}
.profile-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
}
.profile-card__main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #ea580c);
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.profile-meta {
  min-width: 0;
}
.profile-meta__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.profile-name {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}
.profile-meta__sub {
  margin-top: 8px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}
.settle-btn {
  background: #0e9c8d;
  border-color: #0e9c8d;
  flex-shrink: 0;
}
.settle-btn:hover {
  background: #0b8578;
  border-color: #0b8578;
}
.kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  padding: 16px 14px;
  min-height: 92px;
  margin-bottom: 8px;
}
.kpi-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-card__value {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}
.kpi-card__label {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.35;
}
.panel-card {
  height: 100%;
  margin-bottom: 12px;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.profit-panel {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 260px;
}
.profit-panel__chart {
  flex: 1;
  min-width: 0;
}
.profit-panel__legend {
  width: 160px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.legend-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #606266;
}
.legend-amt {
  font-weight: 600;
}
.profit-empty {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profit-empty__title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 6px;
}
.profit-empty__desc {
  font-size: 12px;
  color: #909399;
}
.quick-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.quick-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 8px;
  border-radius: 10px;
  background: #f8fafc;
  cursor: pointer;
  transition: background 0.15s ease;
}
.quick-item:hover {
  background: #eef2ff;
}
.quick-item__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.quick-item__label {
  font-size: 13px;
  color: #303133;
  text-align: center;
  line-height: 1.3;
}
@media (max-width: 768px) {
  .profile-card {
    flex-direction: column;
    align-items: flex-start;
  }
  .quick-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
